const installs = new WeakMap()
const MAX_SETTLE_ROUNDS = 3

const dialogFor = (component) => component?.el?.closest?.('#action-dialog') ?? null

const terminalFor = (action, dialog) => {
  const actionName = dialog.getAttribute('data-action-dialog-action-name')
  const openDialog = dialog.querySelector('.dialog')
  if (!actionName || !openDialog) return null

  return { action, actionName, childIds: new Set(), dialog, openDialog }
}

const terminalIsOpen = (terminal) => !terminal.action.isCancelled()
  && terminal.dialog.isConnected
  && terminal.openDialog.isConnected
  && terminal.dialog.querySelector('.dialog') === terminal.openDialog
  && terminal.dialog.getAttribute('data-action-dialog-action-name') === terminal.actionName

const payloadComponentId = (component) => {
  if (typeof component?.snapshot !== 'string') return null

  try {
    const id = JSON.parse(component.snapshot)?.memo?.id

    return typeof id === 'string' ? id : null
  } catch (_) {
    return null
  }
}

const hasOnlyEmptyCommit = (component) => Array.isArray(component?.calls)
  && component.calls.length === 1
  && component.calls[0]?.method === '$commit'
  && component?.updates !== null
  && typeof component?.updates === 'object'
  && !Array.isArray(component.updates)
  && Object.keys(component.updates).length === 0

const hasOnlyTerminalAndOptionalCommit = (component, terminalName) => {
  if (!Array.isArray(component?.calls) || component.calls.length < 1 || component.calls.length > 2) return false

  const terminalCalls = component.calls.filter(call => call?.method === terminalName)
  const commitCalls = component.calls.filter(call => call?.method === '$commit')

  return terminalCalls.length === 1
    && commitCalls.length <= 1
    && terminalCalls.length + commitCalls.length === component.calls.length
}

const orderedPayloadFor = (components, terminal) => {
  const parentId = terminal.action.component?.id
  if (typeof parentId !== 'string' || terminal.childIds.size === 0) return null

  const entries = components.map((component, index) => ({
    component,
    id: payloadComponentId(component),
    index,
  }))
  const parent = entries.find(({ component, id }) => id === parentId
    && hasOnlyTerminalAndOptionalCommit(component, terminal.action.name))
  if (!parent) return null

  const children = entries.filter(({ id }) => id !== null && terminal.childIds.has(id))
  if (children.length === 0 || children.some(({ component }) => !hasOnlyEmptyCommit(component))) return null
  if (children.every(({ index }) => index < parent.index)) return null

  const childPayloads = new Set(children.map(({ component }) => component))
  const remaining = components.filter(component => !childPayloads.has(component))
  const parentIndex = remaining.indexOf(parent.component)
  if (parentIndex < 0) return null

  remaining.splice(parentIndex, 0, ...children.map(({ component }) => component))

  return remaining
}

const orderMessagesLikePayload = (request, componentIds) => {
  if (
    !request?.messages
    || typeof request.messages.clear !== 'function'
    || typeof request.messages.add !== 'function'
  ) return false

  const messages = [...request.messages]
  if (messages.length !== componentIds.length) return false

  const messagesByComponent = new Map()
  messages.forEach(message => {
    const id = message?.component?.id
    if (typeof id !== 'string') return

    const matches = messagesByComponent.get(id) ?? []
    matches.push(message)
    messagesByComponent.set(id, matches)
  })

  const ordered = []
  for (const id of componentIds) {
    const matches = messagesByComponent.get(id)
    const message = matches?.shift()
    if (!message) return false

    ordered.push(message)
  }

  if ([...messagesByComponent.values()].some(matches => matches.length > 0)) return false

  request.messages.clear()
  ordered.forEach(message => request.messages.add(message))

  return true
}

const installTerminalOrdering = (livewire, orderedTerminals) => {
  const orderingByAction = new WeakMap()
  const removeRequestHook = livewire.hook('request', ({ options }) => {
    if (options === null || typeof options !== 'object' || typeof options.body !== 'string') return

    let payload
    try {
      payload = JSON.parse(options.body)
    } catch (_) {
      return
    }

    if (!Array.isArray(payload?.components)) return

    for (const terminal of orderedTerminals) {
      if (!terminalIsOpen(terminal)) continue

      const components = orderedPayloadFor(payload.components, terminal)
      if (!components) continue

      const componentIds = components.map(payloadComponentId)
      if (componentIds.some(id => id === null)) return

      const originalBody = options.body
      payload.components = components
      options.body = JSON.stringify(payload)
      orderingByAction.set(terminal.action, { componentIds, options, originalBody })

      return
    }
  })
  const removeRequestInterceptor = livewire.interceptRequest(({ request }) => {
    const actions = [...(request?.messages ?? [])]
      .flatMap(message => [...(message?.actions ?? [])])
    const action = actions.find(candidate => orderingByAction.has(candidate))
    if (!action) return

    const ordering = orderingByAction.get(action)
    orderingByAction.delete(action)

    if (!orderMessagesLikePayload(request, ordering.componentIds)) ordering.options.body = ordering.originalBody
  })

  return () => {
    removeRequestInterceptor()
    removeRequestHook()
  }
}

const nestedComponents = (livewire, dialog) => livewire
  .all()
  .filter(component => component?.el && component.el !== dialog && dialog.contains(component.el))

const boundNestedComponents = (component, dialog) => {
  if (typeof component?.getDeepChildrenWithBindings !== 'function') return []

  const children = []
  component.getDeepChildrenWithBindings(child => {
    if (child?.el && child.el !== dialog && dialog.contains(child.el)) children.push(child)
  })

  return children
}

const componentIsDirty = (component) => {
  try {
    return JSON.stringify(component?.canonical) !== JSON.stringify(component?.reactive)
  } catch (_) {
    return false
  }
}

const pendingFor = (pendingByDialog, dialog, component) => {
  const byComponent = pendingByDialog.get(dialog)
  return byComponent?.get(component) ?? new Set()
}

const trackTerminal = (terminalsByDialog, dialog, terminal, onFinish) => {
  let terminals = terminalsByDialog.get(dialog)
  if (!terminals) {
    terminals = new Set()
    terminalsByDialog.set(dialog, terminals)
  }

  terminals.add(terminal)
  onFinish(() => {
    terminals.delete(terminal)
    if (terminals.size === 0) terminalsByDialog.delete(dialog)
  })
}

const cancelTerminals = (terminalsByDialog, dialog) => {
  terminalsByDialog.get(dialog)?.forEach(terminal => {
    if (terminal.canCancel()) terminal.action.cancel()
  })
}

const gateTerminalFire = (terminal, onSend) => {
  const originalFire = terminal.action.fire.bind(terminal.action)
  let ready = false
  let fired = false
  let sent = false

  onSend(() => {
    fired = true
    sent = true
  })
  terminal.action.fire = () => {
    if (!ready || fired || terminal.action.isCancelled()) return
    if (!terminalIsOpen(terminal)) {
      terminal.action.cancel()

      return
    }

    fired = true
    originalFire()
  }

  return {
    hasSent: () => sent,
    markReady: () => { ready = true },
    release: () => {
      ready = true
      terminal.action.fire()
    },
  }
}

const trackPending = (pendingByDialog, dialog, component, promise, onFinish) => {
  let byComponent = pendingByDialog.get(dialog)
  if (!byComponent) {
    byComponent = new Map()
    pendingByDialog.set(dialog, byComponent)
  }

  let pending = byComponent.get(component)
  if (!pending) {
    pending = new Set()
    byComponent.set(component, pending)
  }

  pending.add(promise)
  onFinish(() => {
    pending.delete(promise)
    if (pending.size === 0) byComponent.delete(component)
  })
}

const nestedWork = (livewire, pendingByDialog, dialog) => {
  const work = new Set()

  nestedComponents(livewire, dialog).forEach(component => {
    const pending = pendingFor(pendingByDialog, dialog, component)
    pending.forEach(promise => work.add(promise))

    if (pending.size === 0 && componentIsDirty(component)) {
      work.add(component.$wire.$commit())
    }
  })

  return [...work]
}

const nestedComponentsAreSettled = (livewire, pendingByDialog, dialog) => nestedComponents(livewire, dialog)
  .every(component => pendingFor(pendingByDialog, dialog, component).size === 0 && !componentIsDirty(component))

const settleNestedComponents = async (livewire, pendingByDialog, terminal, initialWork) => {
  let work = initialWork

  for (let round = 0; round < MAX_SETTLE_ROUNDS; round += 1) {
    if (!terminalIsOpen(terminal)) return false
    if (work.length === 0) return true

    await Promise.all(work)
    if (!terminalIsOpen(terminal)) return false
    if (nestedComponentsAreSettled(livewire, pendingByDialog, terminal.dialog)) return true
    if (round === MAX_SETTLE_ROUNDS - 1) return false

    work = nestedWork(livewire, pendingByDialog, terminal.dialog)
  }

  return false
}

export default function installActionDialogCommitGuard(livewire) {
  if (!livewire?.interceptAction || !livewire?.interceptRequest || !livewire?.hook || !livewire?.all) return () => {}
  if (installs.has(livewire)) return installs.get(livewire)

  const pendingByDialog = new WeakMap()
  const terminalsByDialog = new WeakMap()
  const orderedTerminals = new Set()
  const removeTerminalOrdering = installTerminalOrdering(livewire, orderedTerminals)
  const removeInterceptor = livewire.interceptAction(({ action, onFinish, onSend }) => {
    const component = action.component
    const dialog = dialogFor(component)
    if (!dialog) return

    if (component.el === dialog && action.name === 'cancel') {
      cancelTerminals(terminalsByDialog, dialog)
      return
    }

    if (component.el !== dialog && action.name === '$commit') {
      trackPending(pendingByDialog, dialog, component, action.promise, onFinish)
      return
    }

    const terminalAction = action.name === 'next' || action.name === 'run' || action.name === 'runVariant'
    const originatedInDialog = action.origin?.el?.closest?.('#action-dialog') === dialog
    if (!terminalAction || !originatedInDialog || component.el !== dialog) return

    const terminal = terminalFor(action, dialog)
    if (!terminal) return

    const fireGate = gateTerminalFire(terminal, onSend)
    trackTerminal(terminalsByDialog, dialog, {
      action,
      canCancel: () => !fireGate.hasSent(),
    }, onFinish)
    onFinish(() => orderedTerminals.delete(terminal))

    const work = nestedWork(livewire, pendingByDialog, dialog)
    if (work.length === 0) {
      terminal.childIds = new Set(boundNestedComponents(action.component, dialog)
        .map(component => component?.id)
        .filter(id => typeof id === 'string'))
      orderedTerminals.add(terminal)
      fireGate.markReady()

      return
    }

    action.defer()
    void settleNestedComponents(livewire, pendingByDialog, terminal, work)
      .then(shouldRun => {
        if (action.isCancelled()) return
        if (!shouldRun) {
          action.cancel()

          return
        }

        terminal.childIds = new Set(boundNestedComponents(action.component, dialog)
          .map(component => component?.id)
          .filter(id => typeof id === 'string'))
        orderedTerminals.add(terminal)
        fireGate.release()
      })
      .catch(() => {
        if (!action.isCancelled()) action.cancel()
      })
  })

  const dispose = () => {
    removeInterceptor()
    removeTerminalOrdering()
    installs.delete(livewire)
  }
  installs.set(livewire, dispose)

  return dispose
}
